import { isEqual, merge } from 'lodash-es';

import { hasOwnProperty } from './objects';
import { isDefined } from './tsUtils';

interface ArrayMethods {
  [key: string]: any;
}

type ArrayToNumber = (arr: unknown[]) => number;
type ModifyArrayWithIndex = (arr: unknown[], index: number) => unknown[];

/**
 * Returns an array with arrays of the given size.
 *
 * @param myArray {Array} Array to split
 * @param chunkSize {Integer} Size of every group
 */
export function chunkArray(myArray: unknown[], chunkSize: number) {
  const results = [];

  while (myArray.length) {
    results.push(myArray.splice(0, chunkSize));
  }

  return results;
}

/**
 * Sorts an array by another array
 * @param arr The array to be sorted
 * @param order the array containing the order
 * @param key the key to compare by
 */
export function mapOrder(
  arr: Record<string, unknown>[],
  order: string[],
  key: string,
) {
  const map = order.reduce((rArg, v, i) => {
    // ((r[v] = i), r)
    const r = rArg;
    r[v] = i;
    return r;
  }, {} as Record<string, number>);
  return arr.sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    if (
      (typeof aVal === 'string' || typeof aVal === 'number') &&
      (typeof bVal === 'string' || typeof bVal === 'number')
    ) {
      return map[aVal] - map[bVal];
    }
    return 0;
  });
}

/**
 * Create an array of integers starting from zero with
 * arg number length.
 * @param num
 */
export const arrayFromNumber = (num: number): number[] => [
  ...Array(num).keys(),
];

export const doFromNumber =
  <R>(method: keyof ArrayMethods) =>
  (number: number, callBack: (n: number) => R) => {
    const arr: ArrayMethods = arrayFromNumber(number);
    return arr[method]((n: number) => callBack(n));
  };

/**
 * Give a number and a callback and it will map over an array
 * of numbers from 0 to 1 minus your number arg. e.g.
 *
 * mapFromNumber(3, callback) === [0,1,2].map(callback)
 *
 * equivalent:
 *
 * [...Array(num).keys()].map(callback)
 * 
 * TODO - add this type to mapFromNumber
 * <R>(
  number: number,
  callBack: (n: number) => R,
) => R[]
 */
export const mapFromNumber = doFromNumber('map');
export const forEachFromNumber = doFromNumber('forEach');

type IBatchConfig = {
  /**
   * The amount of time in ms is allowed before the process is paused
   * to allow other tasks to be processed.
   */
  maxBatchTime?: number;
  batchDelay?: number;
  onBatchCompletionIterations?: number;
};

type IBatchResolve = {
  isCancelled: boolean;
};

/**
 * A function for batched processing of array data.  Prevents
 * lockup of the main thread.  NOTE: Array will be mutated and
 * progressively emptied (to limit memory impact).
 *
 * @param array - The array to be processed
 * @param processArrayItem - function that processes individual array items.
 * @param getIsCancelled - function that if it returns true will cause the
 *  batched process to stop after the current batch.
 * @param onBatchComplete - function that will be called when the number of
 *  batches specified by onBatchCompletionIterations have completed.
 * @param config - {
 *
 *  maxBatchTime: The amount of time in ms allowed before the process is
 *    paused to allow other tasks to be processed.  Default 30ms.
 *
 *  batchDelay: The minimum amount of time in ms before the next batch is
 *    allowed to commence. Default 5ms.
 *
 *  onBatchCompletionIterations: the number of batches that will complete
 *    before the onBatchComplete function is called.
 *  }
 * @param processId - For debugging.  If supplied will console log the value
 *  and remaining array length to be processed at the start of each batch.
 */
export const batchProcessArray = (
  arrayArg: Array<unknown>,
  processArrayItem: (arrayItem: unknown) => unknown,
  getIsCancelled?: () => boolean,
  onBatchComplete?: () => unknown,
  config: IBatchConfig = {
    maxBatchTime: 8,
    batchDelay: 0,
    onBatchCompletionIterations: 50,
  },
  processId = '',
): Promise<IBatchResolve> => {
  const array = [...arrayArg];
  const { maxBatchTime, batchDelay, onBatchCompletionIterations } = config;
  let batchIterCount = 0;
  let totalBatchCount = 0;
  return new Promise((resolve, reject) => {
    const process = () => {
      let isCancelled = false;
      if (typeof getIsCancelled === 'function') {
        isCancelled = getIsCancelled();
      }
      const endtime = +new Date() + (maxBatchTime || 8);
      totalBatchCount += 1;

      if (processId) {
        // eslint-disable-next-line
        console.log(
          `Batch: ${processId} - Batch Number: ${totalBatchCount} - Remaining items: ${array.length}`,
        );
      }

      do {
        try {
          processArrayItem(array.shift());
        } catch (err) {
          reject(err);
        }
      } while (!isCancelled && array.length > 0 && endtime > +new Date());

      batchIterCount += 1;
      if (batchIterCount === onBatchCompletionIterations) {
        batchIterCount = 0;
        if (typeof onBatchComplete === 'function') {
          onBatchComplete();
        }
      }

      if (array.length > 0 && !isCancelled) {
        setTimeout(process, batchDelay);
      } else if (array.length === 0 || isCancelled) {
        if (typeof onBatchComplete === 'function') {
          onBatchComplete();
        }
        resolve({ isCancelled });
      }
    };

    setTimeout(process, 0);
  });
};

/**
 * Remove at item from an array at the given index.  Returns
 * a new array.
 * @param array
 * @param index
 * @returns
 */
export const removeFromArray: ModifyArrayWithIndex = (array, index) => {
  const { length } = array;
  if (index < 0) {
    return removeFromArray(array, index + length);
  }

  if (index === 0) {
    return array.slice(1, length);
  }

  if (length - 1 === index) {
    return array.slice(0, index);
  }

  return [...array.slice(0, index), ...array.slice(index + 1, length)];
};

/**
 * Removes an item from an array.  If the item
 * is not found in the array - the original array
 * is returned.  Otherwise a new array is returned
 * with the item removed.
 *
 * @param array
 * @param item
 * @returns
 */
export const removeItemFromArray = (
  array: unknown[],
  item: unknown,
): unknown[] => {
  const index = array.indexOf(item);
  if (index === -1) {
    return array;
  }
  return removeFromArray(array, index);
};

/**
 * Get the center index of an array.  If even length,
 * returns the lower index of the middle two.
 * @param array
 */
export const getCenterIndex: ArrayToNumber = (array) => {
  const { length } = array;
  if (length === 0 || length === 1) {
    return 0;
  }
  const isEven = length % 2 === 0;
  if (isEven) {
    return length / 2 - 1;
  }

  return Math.floor(length / 2);
};

/**
 * Sorts an array by taking the outer most elements (index: 0 and length - 1)
 * and swapping them with the inner most available positions - with
 * a gap of fixed elements remaining between the inner swapped positions
 *
 * Modifies the array in place.
 */
export const sortOuterInwardSpaced = (valuesArg: unknown[]) => {
  const values = valuesArg;
  const len = values.length;
  const settled: unknown[] = [];
  const settledPush = (val: number) => {
    if (!settled.includes(val) && val >= 0 && val < len) {
      settled.push(val);
    }
  };
  let chosenIndex = getCenterIndex(values);
  let lowIndex = 0;
  let highIndex = values.length - 1;
  let currIndex = lowIndex;
  let downIndex;
  let upIndex;
  let down = null;
  while (settled.length < values.length && chosenIndex > 0) {
    const a = values[currIndex];
    const b = values[chosenIndex];
    values[currIndex] = b;
    values[chosenIndex] = a;
    settledPush(currIndex);
    settledPush(chosenIndex);

    if (down === null || down) {
      downIndex = chosenIndex - 1;
      settledPush(downIndex);
      settledPush(currIndex + 1);
    }

    if (down !== null && !down) {
      settledPush(currIndex - 1);
    }

    if (down === null || !down) {
      upIndex = chosenIndex + 1;
      settledPush(upIndex);
    }

    if (down === null || down) {
      lowIndex += 1;
    }

    if (!down && down !== null) {
      highIndex -= 1;
    }
    down = !down;
    chosenIndex = down ? (downIndex || 0) - 1 : (upIndex || 0) + 1;
    while (settled.includes(chosenIndex)) {
      if (down) {
        chosenIndex += 1;
      } else {
        chosenIndex -= 1;
      }
    }
    currIndex = down ? highIndex : lowIndex;
    while (settled.includes(currIndex)) {
      if (down) {
        currIndex -= 1;
      } else {
        currIndex += 1;
      }
    }
  }
};

/**
 * Add or merge an object into an array of objects
 * @param arr
 * @param newItem
 * @param key
 * @returns
 */
export const upsert = <D>(
  arr: D[],
  newItem: D,
  key: keyof D = 'id' as keyof D,
) => {
  const identifier = newItem[key];
  let found = false;
  let equalFound = false;
  const newArr = arr.map((item) => {
    if (item[key] === identifier) {
      found = true;
      const areEqual = isEqual(item, newItem);
      if (areEqual) {
        equalFound = true;
        return item;
      }
      return merge({}, item, newItem);
    }
    return item;
  });
  if (equalFound) {
    return { result: arr, merged: true };
  }
  if (found) {
    return { result: newArr, merged: true };
  }
  newArr.push(newItem);
  return { result: newArr, merged: false };
};

export const upsertItemById = <D>(
  state: { items: string[]; itemsById: Record<string, D> },
  newItem: D,
  key = 'id',
) => {
  const { itemsById, items } = state;
  const id = newItem[key as keyof D];
  if (typeof id === 'string' && hasOwnProperty(itemsById, id)) {
    const areEqual = isEqual(itemsById[id], newItem);
    if (areEqual) {
      return state;
    }
    const mergedItem = merge({}, itemsById[id], newItem);
    return { items, itemsById: { ...itemsById, [id]: mergedItem } };
  }
  if (typeof id === 'string') {
    return {
      items: [...items, id],
      itemsById: { ...itemsById, [id]: newItem },
    };
  }
  return state;
};

/**
 * Converts an array of numbers into their
 * relative quantity equivalents of the desiredTotal, while
 * ensuring that they correctly sum to this desiredTotal.
 *
 * e.g.
 * numbers = [2, 4, 1, 3]
 * desiredTotal = 100
 * -> [20, 40, 10, 30]
 *
 */
export const roundLargestRemainder = (
  numbers: number[],
  desiredTotal: number,
) => {
  interface Item {
    floor: number;
    remainder: number;
    index: number;
  }

  const checkZeros = (n: number[]) => {
    let allZero = true;
    n.forEach((i) => {
      if (i !== 0) {
        allZero = false;
      }
    });
    return allZero;
  };

  if (checkZeros(numbers)) {
    return numbers;
  }

  const per = (a: number, total: number) => {
    if (total === 0) {
      return 0;
    }
    return Math.floor((a / total) * desiredTotal);
  };

  const getRemainder = (number: number) => {
    const remainder = number - Math.floor(number);
    return remainder.toFixed(4);
  };

  const total = numbers.reduce((acc, curr) => acc + curr, 0);

  const result = numbers
    .map((number, index) => {
      return {
        floor: per(number, total),
        remainder: parseFloat(getRemainder(number)),
        index,
      };
    })
    .sort((a: Item, b: Item) => {
      return b.remainder - a.remainder;
    });

  const lowerSum = result.reduce((sum, current) => {
    return sum + current.floor;
  }, 0);

  const delta = desiredTotal - lowerSum;
  for (let i = 0; i < delta; i += 1) {
    result[i].floor += 1;
  }

  return result
    .sort((a, b) => {
      return a.index - b.index;
    })
    .map((item) => {
      return item.floor;
    });
};

export const getFromMaybeArray = <D>(
  maybeArray: (D | null | undefined)[] | null | undefined,
  index: number,
  defaultVal: D,
) => {
  const arr = (maybeArray || []).filter(isDefined);
  if (arr.length - 1 < index) {
    return defaultVal;
  }
  return arr[index];
};

export const createArrayValueMaker = <T>() => {
  // Closure variables to store the last item and its associated array
  let lastItem: T | undefined;
  let lastArray: T[] | undefined;

  // The returned function that checks the item and returns the appropriate array
  const arrayMaker = (item: T): T[] => {
    // Check if item is the same as lastItem using strict equality for primitives or reference check for objects
    if (item === lastItem) {
      // Return the last array if item is the same
      return lastArray as T[];
    }
    // Update lastItem and lastArray with the new item and its array
    lastItem = item;
    lastArray = [item];
    return lastArray;
  };

  return arrayMaker;
};
